import * as d3 from 'd3'
import { motion } from 'motion/react'
import * as React from 'react'

import { chartPalette, cn } from '@/utils'
import { ChartControls } from './Controls'
import { useChartBase } from './Hooks'
import { ChartTooltip } from './Tooltip'
import type { ChartExportProps } from './types'

interface DonutDataPoint {
  label: string
  value: number
  color?: string
}

interface DonutArc extends d3.PieArcDatum<DonutDataPoint> {
  arc: string | undefined
  color: string
  label: string
  percentage: string
}

interface DonutChartProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ChartExportProps {
  data?: DonutDataPoint[]
  className?: string
  size?: number
  innerRadius?: number
  colors?: string[]
  showLabels?: boolean
  showLegend?: boolean
  centerLabel?: string
  centerValue?: string | number
  valueFormatter?: (value: number) => string
  onChartReady?: (params: {
    containerRef: React.RefObject<HTMLDivElement | null>
    isFullscreen: boolean
    toggleFullscreen: () => Promise<void>
  }) => void
  showHoverControls?: boolean
}

const DonutChart = React.forwardRef<HTMLDivElement, DonutChartProps>(
  (
    {
      data = [],
      className,
      size = 300,
      innerRadius = 0.6,
      colors = chartPalette,
      showLabels = true,
      showLegend = true,
      centerLabel,
      centerValue,
      valueFormatter = (value: number) => value.toLocaleString(),
      onChartReady,
      projectName,
      title,
      filters,
      schema,
      dateRange,
      filename = 'chart-export.png',
      showHoverControls = false,
      ...props
    },
    ref,
  ) => {
    const {
      containerRef,
      hoveredData,
      setHoveredData,
      isFullscreen,
      toggleFullscreen,
      fullscreenError,
      fullscreenSupported,
    } = useChartBase(onChartReady)
    const [dimensions, setDimensions] = React.useState({
      width: size,
      height: size,
    })

    React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement)

    React.useEffect(() => {
      if (!containerRef.current) return
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        const { width } = entry.contentRect
        const chartSize = Math.min(width, size)
        setDimensions({ width: chartSize, height: chartSize })
      })
      resizeObserver.observe(containerRef.current)
      return () => resizeObserver.disconnect()
    }, [size])

    const arcs = React.useMemo(() => {
      const validData = data.filter((d) => d.value > 0)
      const pie = d3
        .pie<DonutDataPoint>()
        .value((d) => d.value)
        .sort(null)

      const radius = Math.min(dimensions.width, dimensions.height) / 2
      const arcGenerator = d3
        .arc<d3.PieArcDatum<DonutDataPoint>>()
        .innerRadius(radius * innerRadius)
        .outerRadius(radius - 10)

      const pieData = pie(validData)
      const total = d3.sum(validData, (d) => d.value)

      return pieData.map(
        (d, i): DonutArc => ({
          ...d,
          arc: arcGenerator(d) ?? undefined,
          color: validData[i].color || colors[i % colors.length],
          label: validData[i].label,
          percentage: ((d.value / total) * 100).toFixed(1),
        }),
      )
    }, [data, dimensions, innerRadius, colors])

    const handlePointerMove = (arcData: DonutArc) => {
      setHoveredData(arcData)
    }

    const handlePointerLeave = () => {
      setHoveredData(null)
    }

    if (!data.length) return null

    const centerX = dimensions.width / 2
    const centerY = dimensions.height / 2

    // Shrink the center text to fit the donut hole so it never overlaps the ring on a small chart.
    // Capped at the original sizes (24 / 14) so large donuts look unchanged.
    const radius = Math.min(dimensions.width, dimensions.height) / 2
    const holeDiameter = 2 * radius * innerRadius
    const valueText = centerValue != null ? String(centerValue) : ''
    const valueFontSize = valueText
      ? Math.max(
          11,
          Math.min(
            (holeDiameter * 0.85) / Math.max(valueText.length * 0.6, 1),
            24,
          ),
        )
      : 0
    const labelFontSize = Math.min(
      14,
      Math.max(9, (valueFontSize || 20) * 0.55),
    )

    return (
      <div
        ref={containerRef}
        className={cn(
          'w-full relative flex flex-col items-center',
          isFullscreen && 'bg-white',
          className,
        )}
        {...props}
      >
        <svg
          role="img"
          aria-label="Donut chart"
          width={dimensions.width}
          height={dimensions.height}
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        >
          <g transform={`translate(${centerX}, ${centerY})`}>
            {arcs.map((arc, i) => (
              <motion.path
                key={i}
                d={arc.arc}
                fill={arc.color}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: hoveredData === arc ? 1 : 0.9,
                  scale: hoveredData === arc ? 1.05 : 1,
                }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                onPointerEnter={() => handlePointerMove(arc)}
                onPointerLeave={handlePointerLeave}
                className="cursor-pointer transition-all"
                style={{ transformOrigin: 'center' }}
              />
            ))}
          </g>

          {/* Center label */}
          {(centerLabel || centerValue) && (
            <g transform={`translate(${centerX}, ${centerY})`}>
              <text
                textAnchor="middle"
                dominantBaseline="middle"
                className="font-bold fill-current"
                style={{ fontSize: valueFontSize }}
                y={centerLabel ? -labelFontSize * 0.8 : 0}
              >
                {valueText}
              </text>
              {centerLabel && (
                <text
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-muted-foreground"
                  style={{ fontSize: labelFontSize }}
                  y={valueText ? valueFontSize * 0.6 : 0}
                >
                  {centerLabel}
                </text>
              )}
            </g>
          )}
        </svg>

        {/* Legend */}
        {showLegend && (
          <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {arcs.map((arc, i) => (
              <div
                key={i}
                className="flex items-center gap-2 cursor-pointer opacity-90 hover:opacity-100"
                onPointerEnter={() => handlePointerMove(arc)}
                onPointerLeave={handlePointerLeave}
              >
                <div
                  className="w-3 h-3 rounded-sm shrink-0"
                  style={{ backgroundColor: arc.color }}
                />
                <span className="truncate">{arc.label}</span>
                <span className="text-muted-foreground ml-auto">
                  {arc.percentage}%
                </span>
              </div>
            ))}
          </div>
        )}

        {showHoverControls && (
          <ChartControls
            containerRef={containerRef}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            fullscreenError={fullscreenError}
            fullscreenSupported={fullscreenSupported}
            filename={filename}
            projectName={projectName}
            title={title}
            filters={filters}
            schema={schema}
            dateRange={dateRange}
          />
        )}

        <ChartTooltip
          active={!!hoveredData}
          position={hoveredData ? { x: centerX, y: centerY - 50 } : undefined}
        >
          {hoveredData && (
            <>
              <div className="font-semibold">{hoveredData.label}</div>
              <div className="text-muted-foreground">
                {valueFormatter(hoveredData.value)} ({hoveredData.percentage}%)
              </div>
            </>
          )}
        </ChartTooltip>
      </div>
    )
  },
)
DonutChart.displayName = 'DonutChart'

export { DonutChart }
